import { Dispatch } from 'react';
import {
    BrowserStorageKey,
    BucketType,
    FileLog,
    IThrowErrorUploadType,
    IUploadType,
    FileOfBucket,
    UploadObjectFile,
    S3StatusBucket,
    S3StatusObjectFile,
    EFileExtensionUpload,
} from '../types';
import { AuthenticatedPayload } from '../store';
import { TypeCommon, BaseCommon, EquipmentCommon } from '../common';
import { AmplifyS3Config, AWSS3Config } from '../custom/type';
import { BrowserStorageUtil, ConfigurationUtil } from '../utils';
import s3Client from '../custom/amplifys3.client';
import {
    APP_ACCEPT_EXTENSION_FILES_CSV,
    CHARACTER_SLASH,
    FIVE_HUNDRED_MB_IN_BYTES,
    NOP_FIVE_MINUTES_MS,
    NOP_THREE_MINUTES_MS,
    ONE_GB_IN_BYTES,
    PATH_INDEX,
    S3_CHECK_INTERVAL_MS,
} from 'src/common/base.common';

const fetchEquipmentLogFromS3 = async (fileExtensionUpload: EFileExtensionUpload): Promise<S3StatusBucket> => {
    const bucketType = fileExtensionUpload === EFileExtensionUpload.DB3 ? BucketType.SQLITE : BucketType.TIMESTREAM;
    const dataS3 = await fetchSingleDataFromS3(bucketType, fileExtensionUpload);
    return dataS3;
};

const fetchBlockingFromS3 = async (): Promise<S3StatusBucket> => {
    return await fetchSingleDataFromS3(BucketType.BLOCKING, EFileExtensionUpload.DB3);
};

const fetchSingleDataFromS3 = async (type: BucketType, fileExtensionUpload: EFileExtensionUpload): Promise<S3StatusBucket> => {
    try {
        const objects = (await s3Client.getInstance(type).list()).items as unknown as S3StatusObjectFile[];
        const bucketName = s3Client.getConfigOfInstance(type)?.AWSS3.bucket;
        return transformToS3StatusBucket(objects, bucketName, fileExtensionUpload);
    } catch (error) {
        console.error('FETCH_S3::ERROR::', error);
        throw error;
    }
};

const updateBlockingFlag = async (value: boolean): Promise<void> => {
    try {
        if (value === true) {
            await s3Client.getInstance(BucketType.BLOCKING).put(BaseCommon.FILENAME_BLOCKING_FLAG, BaseCommon.FILENAME_BLOCKING_FLAG, {
                errorCallback: (error: any) => {
                    throw error;
                },
            });
        } else {
            await s3Client.getInstance(BucketType.BLOCKING).remove(BaseCommon.FILENAME_BLOCKING_FLAG);
        }
    } catch (error: any) {
        console.error('BLOCKING_FLAG::ERROR::', error);
        throw error;
    }
};

const transformToS3StatusBucket = (
    data: S3StatusObjectFile[],
    name: string | undefined,
    fileExtensionUpload: EFileExtensionUpload,
): S3StatusBucket => {
    const objects = data
        .map((item: S3StatusObjectFile) => {
            const split = item.path.split(BaseCommon.CHARACTER_SLASH);
            const format = fileExtensionUpload === EFileExtensionUpload.CSV ? PATH_INDEX.CSV : PATH_INDEX.DB3;
            item.equipmentId = split[format.EQUIPMENT_ID_LEVEL];
            item.key = split[format.KEY_LEVEL];
            return item;
        })
        .filter((item: S3StatusObjectFile) => item.key && item.key.trim());

    const totalSize = data.reduce((total: number, item: S3StatusObjectFile) => total + item.size, 0);
    return {
        name,
        totalFile: objects.length,
        totalSize,
        objects,
    };
};

const buildResourceEntry = (lcFiles: FileLog[], fileExtensionUpload: EFileExtensionUpload): FileOfBucket => {
    const objects = lcFiles.reduce((result: UploadObjectFile[], file: FileLog) => transformFileToResource(result, file, fileExtensionUpload), []);
    const bucketName = fileExtensionUpload === EFileExtensionUpload.DB3 ? BucketType.SQLITE : BucketType.TIMESTREAM;
    const bucketClient = s3Client.getConfigOfInstance(bucketName).AWSS3.bucket;
    const resources: FileOfBucket = buildResourceForUpload(bucketClient, objects);
    return resources;
};

const transformFileToResource = (result: UploadObjectFile[], file: FileLog, fileExtensionUpload: EFileExtensionUpload): UploadObjectFile[] => {
    const folder = file.path?.length
        ? file.path?.split(file.name)[0].split(BaseCommon.CHARACTER_SLASH).slice(-2, -1).join(BaseCommon.CHARACTER_SLASH)
        : BaseCommon.EMPTY_STRING;
    const isCsv = fileExtensionUpload === EFileExtensionUpload.CSV;
    const regexFile = isCsv ? EquipmentCommon.REGEX_CSV_FILE : EquipmentCommon.REGEX_DB3_FILE;
    const isMatchRegex = file.name.match(regexFile);
    if (!isMatchRegex) {
        return result;
    }
    const fileName = isCsv
        ? file.name.replace(
              EquipmentCommon.REGEX_CSV_FILE_PART,
              (_, base, index) => `${base}${EquipmentCommon.SUFFIX_CSV_FILE_PART}${index}${APP_ACCEPT_EXTENSION_FILES_CSV[0]}`,
          )
        : file.name;
    const folderName = (isCsv ? EquipmentCommon.PREFIX_LOG_FOLDER_CSV : BaseCommon.EMPTY_STRING) + folder;
    const isStartWithNameOfFolder = file.name.startsWith(folderName);
    if (isStartWithNameOfFolder) {
        result.push({
            isError: false,
            size: file.size,
            filename: fileName,
            lastModified: file.lastModified,
            progress: 0,
            folder,
            file,
        });
        return result;
    }

    return result;
};

const buildResourceForUpload = (s3Provider: string, objects: UploadObjectFile[]): FileOfBucket => {
    const totalSize = objects.reduce((total: number, item: UploadObjectFile) => total + item.size, 0);
    return {
        name: s3Provider,
        isError: false,
        progress: 0,
        totalFile: objects.length,
        objects,
        totalSize,
    };
};

const wait = (ms: number): Promise<void> => new Promise(resolve => setTimeout(resolve, ms));

const waitForDb3LogDeletion = async (targetEquipmentId: string, fileSizeBytes: number, bucketName?: string): Promise<void> => {
    if (!bucketName) {
        return;
    }
    if (fileSizeBytes > ONE_GB_IN_BYTES) {
        await wait(NOP_FIVE_MINUTES_MS);
    } else if (fileSizeBytes > FIVE_HUNDRED_MB_IN_BYTES) {
        await wait(NOP_THREE_MINUTES_MS);
    }
    while (true) {
        const listResult = await s3Client.getInstanceByBucketName(bucketName).list();
        const db3LogExisted = listResult.items.some(item => {
            const [equipmentId, fileName] = item.path.split(CHARACTER_SLASH);
            return equipmentId === targetEquipmentId && EquipmentCommon.REGEX_DB3_SVLOG_FILE.test(fileName);
        });
        if (!db3LogExisted) {
            return;
        }
        await wait(S3_CHECK_INTERVAL_MS);
    }
};

const calculateUploadingProgress = async (
    uploadingFiles: FileOfBucket,
    equipmentId: string,
    fileExtensionUpload: EFileExtensionUpload,
    dispatch: Dispatch<AuthenticatedPayload>,
): Promise<void> => {
    const utcTime = new Date().toISOString().replace(BaseCommon.REGEX_ISO_DATE, BaseCommon.CHARACTER_ISO_DATE_SUFFIX);
    for await (const [index, object] of uploadingFiles.objects.entries()) {
        try {
            const basePath = [equipmentId, object.filename];
            const isLastFile = index === uploadingFiles.objects.length - 1;
            const pathArray = fileExtensionUpload === EFileExtensionUpload.CSV ? [utcTime, ...basePath] : basePath;
            await s3Client.getInstanceByBucketName(uploadingFiles.name as string).put(pathArray.join(BaseCommon.CHARACTER_SLASH), object.file, {
                onProgress: (progress: any) => handleProgressCallback(uploadingFiles, object, progress, dispatch),
                errorCallback: handleErrorCallback,
            });
            if (EquipmentCommon.REGEX_DB3_SVLOG_FILE.test(object.filename) && !isLastFile) {
                await waitForDb3LogDeletion(equipmentId, object.size, uploadingFiles.name);
            }
        } catch (err: any) {
            const uploaded = handleProgressError(uploadingFiles, object);
            const payload: IThrowErrorUploadType = {
                uploadingFile: uploaded,
            };
            dispatch(TypeCommon.setThrowErrorUploadType(payload));
        }
    }
};

const handleErrorCallback = (err: any): void => {
    throw err;
};

const handleProgressError = (upload: FileOfBucket, object: UploadObjectFile): FileOfBucket => {
    object.isError = true;
    object.file = null;
    upload.isError = true;
    const newKb = object.size - (object.progress * object.size) / BaseCommon.COMPLETED_PERCENTAGE_NUM;
    object.progress = BaseCommon.COMPLETED_PERCENTAGE_NUM;
    upload = calculateProgressForBucket(upload, newKb);
    return upload;
};

const handleProgressCallback = (upload: FileOfBucket, object: UploadObjectFile, progress: any, dispatch: Dispatch<AuthenticatedPayload>): void => {
    const total = progress.totalBytes;
    if (total === 0) {
        return;
    }

    const oldProgress = object.progress;
    const newProgress = (progress.transferredBytes / total) * BaseCommon.COMPLETED_PERCENTAGE_NUM;

    if (newProgress < 99 && Math.trunc(newProgress) === Math.trunc(object.progress)) {
        return;
    }

    object.progress = newProgress;

    // Clear file if uploaded successful
    if (object.progress === BaseCommon.COMPLETED_PERCENTAGE_NUM) {
        object.file = null;
    }

    const newKb = progress.loaded - (oldProgress * progress.totalBytes) / BaseCommon.COMPLETED_PERCENTAGE_NUM;
    // calculate and update the progress for bucket
    upload = calculateProgressForBucket(upload, newKb);
    const payload: IUploadType = {
        uploadingFile: upload,
    };
    dispatch(TypeCommon.setUploadType(payload));
};

const isCompletedProgress = (uploadingFiles: FileOfBucket): boolean => {
    if (isEmptyLocalFiles(uploadingFiles)) {
        return false;
    }
    return isCompletedUploaded(uploadingFiles);
};

const isCompletedUploaded = (uploadingFiles: FileOfBucket): boolean => {
    let totalProgress = BaseCommon.COMPLETED_PERCENTAGE_NUM;
    if (uploadingFiles.objects.length !== BaseCommon.DEFAULT_OBJECT_LENGTH) {
        const eachProgress = uploadingFiles.objects.reduce((_total: number, uploadFile: UploadObjectFile) => {
            return _total + uploadFile.progress;
        }, BaseCommon.ZERO_NUMBER);
        const totalSubProgress = eachProgress / uploadingFiles.objects.length;
        totalProgress = totalSubProgress;
    }

    return totalProgress === BaseCommon.COMPLETED_PERCENTAGE_NUM;
};

const isEmptyLocalFiles = (uploadingFiles: FileOfBucket): boolean => {
    return uploadingFiles.objects.length === BaseCommon.DEFAULT_OBJECT_LENGTH;
};

const calculateProgressForBucket = (upload: FileOfBucket, newKb: number): FileOfBucket => {
    const progressBucket = upload;

    const uploadedProgress = progressBucket.progress + (newKb / progressBucket.totalSize) * BaseCommon.COMPLETED_PERCENTAGE_NUM;
    const checkIsAllFilesUploaded = progressBucket.objects.some((file: UploadObjectFile) => file.progress < BaseCommon.COMPLETED_PERCENTAGE_NUM);
    upload = {
        ...progressBucket,
        progress:
            Math.ceil(uploadedProgress) > BaseCommon.COMPLETED_PERCENTAGE_NUM || !checkIsAllFilesUploaded
                ? BaseCommon.COMPLETED_PERCENTAGE_NUM
                : uploadedProgress,
    };

    return upload;
};

const isErrorAnyFileUploaded = (uploadingFiles: FileOfBucket): boolean => {
    if (isEmptyLocalFiles(uploadingFiles)) {
        return false;
    }
    return isAnyFileError(uploadingFiles);
};

const isErrorAllFilesUploaded = (uploadingFiles: FileOfBucket): boolean => {
    if (isEmptyLocalFiles(uploadingFiles)) {
        return false;
    }
    return isAllFilesError(uploadingFiles);
};

const isAllFilesError = (uploadingFiles: FileOfBucket): boolean => {
    return (
        uploadingFiles.isError &&
        uploadingFiles.objects.every((subFile: UploadObjectFile) => {
            return subFile.isError;
        })
    );
};

const isAnyFileError = (uploadingFiles: FileOfBucket): boolean => {
    return uploadingFiles.objects.length !== BaseCommon.DEFAULT_OBJECT_LENGTH && uploadingFiles.isError;
};

const getBucketAndRegion = (url: string): AWSS3Config => {
    let bucket = '';
    let region = '';
    try {
        const urlParser = new URL(url);
        const pathSplit = urlParser.pathname.split(BaseCommon.CHARACTER_SLASH);
        bucket = pathSplit[pathSplit.length - 1];
        region = urlParser.searchParams.get(BaseCommon.STRING_REGION) as string;
    } catch (error) {
        console.error('ERROR:getBucketAndRegion:: ', error);
    }
    return ConfigurationUtil.buildS3Config(bucket, region);
};

const retrieveConfigFromSession = (configs: AmplifyS3Config<BucketType>[]): (AmplifyS3Config<BucketType> | null)[] => {
    const region = BrowserStorageUtil.retrieveFromSession<string>(BrowserStorageKey.S3_REGION) ?? BaseCommon.EMPTY_STRING;
    const configIntoSession = configs.reduce((bucketConfigs: (AmplifyS3Config<BucketType> | null)[], config: AmplifyS3Config<BucketType>) => {
        const bucketName = BrowserStorageUtil.retrieveFromSession<string>(config.sessionKey) ?? BaseCommon.EMPTY_STRING;
        if (!bucketName || !region) {
            bucketConfigs.push(null);
        } else {
            console.log('RetrieveConfigFromSession:: ', { bucketName, region });
            const s3Config: AmplifyS3Config<BucketType> = ConfigurationUtil.buildAmplifyS3Config(config.name, bucketName, region, config.sessionKey);
            bucketConfigs.push(s3Config);
        }
        return bucketConfigs;
    }, []);

    return configIntoSession;
};

const storeConfigToSession = (config: AmplifyS3Config<BucketType>): void => {
    BrowserStorageUtil.storeToSession(BrowserStorageKey.S3_REGION, config.AWSS3.region);
    BrowserStorageUtil.storeToSession(config.sessionKey, config.AWSS3.bucket);
};

const initialConfigToSession = (configs: AmplifyS3Config<BucketType>[]): AmplifyS3Config<BucketType>[] => {
    const configIntoSession = retrieveConfigFromSession(configs);
    const hasNullValue = configIntoSession.some((item: AmplifyS3Config<BucketType> | null) => item === null);

    if (hasNullValue) {
        configs.forEach((config: AmplifyS3Config<BucketType>) => storeConfigToSession(config));
    }
    return retrieveConfigFromSession(configs) as AmplifyS3Config<BucketType>[];
};

export {
    buildResourceEntry,
    fetchEquipmentLogFromS3,
    fetchBlockingFromS3,
    calculateUploadingProgress,
    isCompletedProgress,
    isEmptyLocalFiles,
    isErrorAllFilesUploaded,
    isErrorAnyFileUploaded,
    getBucketAndRegion,
    retrieveConfigFromSession,
    storeConfigToSession,
    initialConfigToSession,
    updateBlockingFlag,
};
